import type { OrderModel } from 'src/types/order';
import type { CustomerModel } from 'src/types/customer';

import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useMemo, useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  Alert,
  Stack,
  Table,
  Button,
  Tooltip,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  IconButton,
  Typography,
  TableContainer,
} from '@mui/material';

import { useMedias } from 'src/hooks/medias/useMedias';
import { useDebounce } from 'src/hooks/common/useDebounce';
import { useCustomers } from 'src/hooks/customers/useCustomers';
import { useCategories } from 'src/hooks/categories/use-categories';

import { fDate } from 'src/utils/format-time';
import { fCurrency } from 'src/utils/format-number';

import { countries } from 'src/assets/data';

import { Form } from 'src/components/hook-form';
import { Iconify } from 'src/components/iconify';

import OrderBilling from './order-new-edit-billing';
import OrderDetails from './order-new-edit-details';
import { useOrderItems } from './hooks/use-order-items';
import { useBillingForm } from './hooks/use-billing-form';
import { useOrderSubmit } from './hooks/use-order-submit';
import { useOrderPreload } from './hooks/use-order-preload';
import OrderBillingDetails from './order-new-edit-billing-details';
import { NewOrderSchema, PaymentMethodEnum } from './types/order-form.types';

import type { NewOrderSchemaType } from './types/order-form.types';

// Re-export for backward compatibility with other components
export { PaymentMethodEnum } from './types/order-form.types';
export type { BillingSchemaType, NewOrderSchemaType, CombinedBillingType, PlatformBillingSchemaType } from './types/order-form.types';

type OrderNewEditFormProps = {
  currentOrder?: OrderModel;
};

export function OrderNewEditForm({ currentOrder }: OrderNewEditFormProps) {
  const isEditMode = !!currentOrder;

  // =============================================
  // Media & Customer search
  // =============================================
  const [searchMedia, setSearchMedia] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodEnum>(PaymentMethodEnum.Pending);

  const debouncedSearchMedia = useDebounce(searchMedia, 500);

  const { medias, isLoading } = useMedias(1, 50, {
    mediaId: undefined,
    name: '',
    email: '',
    linkType: null,
    mediaType: null,
    categories: [],
    country: [],
    language: '',
    redactionField: null,
    active: true,
    drMin: null,
    drMax: null,
    daMin: null,
    daMax: null,
    tfMin: null,
    tfMax: null,
    trafficMin: null,
    trafficMax: null,
    priceMin: null,
    priceMax: null,
    url: '',
    searchTerm: debouncedSearchMedia || undefined,
  });

  const { data: customerOptions, loading: loadingCustomers } = useCustomers({
    searchQuery: searchCustomer,
  });

  const { loading: loadingCategories } = useCategories();

  // =============================================
  // Order form (details)
  // =============================================
  const defaultLimitDate = useMemo(() => dayjs().add(7, 'day'), []);

  const defaultValues = useMemo(
    () => ({
      mediaId: undefined,
      price: 0,
      currency: 0,
      limitDate: defaultLimitDate,
      categoryId: undefined,
      finalUrl: '',
      requireRedaction: false,
      extraRedaction: false,
      wordCount: 600,
    }),
    [defaultLimitDate]
  );

  const methods = useForm<NewOrderSchemaType>({
    resolver: zodResolver(NewOrderSchema),
    defaultValues,
  });

  const { watch, setValue } = methods;
  const watchedMediaField = watch('mediaId');
  const wordCount = watch('wordCount');

  // =============================================
  // Billing form
  // =============================================
  const { billingMethod, setBillingMethod, billingMethods, validateBillingForm } = useBillingForm();

  // =============================================
  // Order items (create mode cart)
  // =============================================
  const {
    orderItems,
    editingItemIndex,
    onAddOrUpdateItem,
    handleRemoveItem,
    handleEditItem,
    handleCancelEdit,
  } = useOrderItems(methods, medias?.data || []);

  // =============================================
  // Pre-load data in edit mode
  // =============================================
  useOrderPreload({
    currentOrder,
    isEditMode,
    methods,
    billingMethods,
    medias: medias?.data || [],
    setBillingMethod,
    setPaymentMethod,
    setSearchMedia,
  });

  // =============================================
  // Auto-fill costPrice from selected media
  // =============================================
  useEffect(() => {
    if (!watchedMediaField?.value) return;

    const selectedMedia = medias?.data?.find((media) => media.id === watchedMediaField.value);

    if (selectedMedia?.costPrice !== undefined && selectedMedia.costPrice !== null) {
      setValue('costPrice', selectedMedia.costPrice);
    }
    if (selectedMedia?.costPriceCurrency !== undefined && selectedMedia.costPriceCurrency !== null) {
      setValue('costPriceCurrency', selectedMedia.costPriceCurrency);
    }
  }, [watchedMediaField, medias?.data, setValue]);

  // =============================================
  // Submit handler
  // =============================================
  const { handleSubmit: submitOrder, isProcessing } = useOrderSubmit({
    isEditMode,
    currentOrder,
    orderItems,
    billingMethod,
    paymentMethod,
    methods,
    validateBillingForm,
    billingMethods,
  });

  // =============================================
  // Customer selection handler
  // =============================================
  const handleCustomerSelect = (customer: CustomerModel | null) => {
    if (!customer) return;

    const { setValue: setBillingValue } = billingMethods;

    setBillingValue('customerId', customer.id);
    setBillingValue('firstName', customer.name || '');
    setBillingValue('lastName', customer.surname || '');
    setBillingValue('company', customer.company || '');
    setBillingValue('email', customer.email || '');
    setBillingValue('phone', customer.phone || '');
    setBillingValue('nif', customer.nif || '');

    if (customer.country) {
      const foundCountry = countries.find(
        (c) => c.label.toLowerCase() === customer.country?.toLowerCase()
      );
      setBillingValue('country', foundCountry?.label || customer.country);
    } else {
      setBillingValue('country', '');
    }

    setBillingValue('city', customer.city || '');
    setBillingValue('postalCode', customer.postalCode || '');
    setBillingValue('address', customer.address || '');
  };

  // =============================================
  // Render
  // =============================================
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
      <Box sx={{ width: { xs: '100%', md: '60%' } }}>
        <Form methods={methods} onSubmit={onAddOrUpdateItem}>
          {/* Edit mode header */}
          {isEditMode && (
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Editando Pedido #{currentOrder?.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Modifica los detalles del pedido y haz clic en &quot;Actualizar Pedido&quot; para
                guardar los cambios.
              </Typography>
            </Card>
          )}

          {/* Item editing indicator (create mode only) */}
          {!isEditMode && editingItemIndex >= 0 && (
            <Alert
              severity="info"
              sx={{ mb: 3 }}
              action={
                <Button color="inherit" size="small" onClick={handleCancelEdit}>
                  Cancelar edicion
                </Button>
              }
            >
              Editando item: <strong>{orderItems[editingItemIndex]?.mediaName}</strong>. Modifica
              los campos y haz clic en &quot;Guardar Cambios&quot;.
            </Alert>
          )}

          {/* Order details form */}
          <OrderDetails
            methods={methods}
            mediaOptions={medias?.data || []}
            loadingMedia={isLoading}
            categoryOptions={[]}
            loadingCategories={loadingCategories}
            onSearchMedia={setSearchMedia}
            selectedPlatformId={
              billingMethod === 'platform' ? billingMethods.watch('platformId')?.value : undefined
            }
            isEditMode={isEditMode}
            isEditingItem={editingItemIndex >= 0}
            onCancelEditItem={handleCancelEdit}
          />

          {/* Order items table (create mode only) */}
          {!isEditMode && orderItems.length > 0 && (
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Items del Pedido
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Medio</TableCell>
                      <TableCell>Precio</TableCell>
                      <TableCell>Fecha Limite</TableCell>
                      <TableCell>Url Final</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.mediaName}</TableCell>
                        <TableCell>{`${fCurrency(item.price + (item.categoryCommission || 0))}`}</TableCell>
                        <TableCell>{item.limitDate ? fDate(item.limitDate) : '-'}</TableCell>
                        <TableCell>{item.finalUrl ? 'Si' : 'No'}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                color={editingItemIndex === index ? 'primary' : 'default'}
                                onClick={() => handleEditItem(index)}
                                disabled={editingItemIndex >= 0 && editingItemIndex !== index}
                              >
                                <Iconify icon="solar:pen-bold" width={18} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(item)}
                              >
                                <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </Form>

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <LoadingButton
            variant="contained"
            onClick={submitOrder}
            disabled={(isEditMode ? false : orderItems.length === 0) || isProcessing}
            loading={isProcessing}
          >
            {isEditMode ? 'Actualizar Pedido' : 'Crear Pedido'}
          </LoadingButton>
        </Stack>
      </Box>

      {/* Billing panel */}
      <Box sx={{ width: { xs: '100%', md: '40%' } }}>
        <OrderBillingDetails
          billingMethods={billingMethods}
          billingMethod={billingMethod}
          onBillingMethodChange={setBillingMethod}
          customerOptions={customerOptions || []}
          loadingCustomers={loadingCustomers || false}
          onSearchCustomer={setSearchCustomer}
          onCustomerSelect={handleCustomerSelect}
          onBillingSubmit={submitOrder}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={setPaymentMethod}
        />

        <OrderBilling
          orderItems={orderItems}
          paymentMethod={paymentMethod}
          wordCount={wordCount}
          onRemoveItem={handleRemoveItem}
          billingCountry={billingMethods.watch('country')}
        />
      </Box>
    </Box>
  );
}
